/**
 * services/api.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Main API service — assembles all domain modules into one ApiService class.
 *
 * ARCHITECTURE — domain files (each ~100-200 lines):
 *   apiUtils.js              — URL helpers, fallback images
 *   authApi.js               — login, signup, logout, session
 *   adsApi.js                — ads, categories, jobs
 *   socialApi.js             — likes, follows, seller profiles
 *   offersApi.js             — offer creation and negotiation
 *   messagesApi.js           — DMs, ratings, notifications
 *   shopsApi.js              — shop management
 *   profileApi.js            — profile update, verification, password reset
 *   chatApi.js               — live support chat
 *   referralsApi.js          — referral stats, commissions, withdrawals
 *   adminUsersApi.js         — admin: users, logs, shops
 *   adminAdsApi.js           — admin: ad plans and validity
 *   adminConversationsApi.js — admin: conversation moderation
 *
 * USAGE — unchanged, no refactor needed in any consuming file:
 *   import apiService from './services/api';
 *   await apiService.fetchAds();
 *   await apiService.login(credentials);
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL, FALLBACK_IMAGE, FALLBACK_THUMB, resolveImageUrl, resolveVideoUrl, resolveAvatarUrl, _isHtmlResponse } from './apiUtils.js';
import { methods as authMethods          } from './authApi.js';
import { methods as adsMethods           } from './adsApi.js';
import { methods as socialMethods        } from './socialApi.js';
import { methods as offersMethods        } from './offersApi.js';
import { methods as messagesMethods      } from './messagesApi.js';
import { methods as shopsMethods         } from './shopsApi.js';
import { methods as profileMethods       } from './profileApi.js';
import { methods as chatMethods          } from './chatApi.js';
import { methods as referralsMethods     } from './referralsApi.js';
import { methods as adminUsersMethods    } from './adminUsersApi.js';
import { methods as adminAdsMethods      } from './adminAdsApi.js';
import { methods as adminConvosMethods   } from './adminConversationsApi.js';

// ── ApiService class ──────────────────────────────────────────────────────────
// Internal helpers (_getToken, _adminHeaders) and data transformers
// (transformAdData, transformSingleAd) stay here because they are used
// by fetchAds and getAdById — the core methods that all other code depends on.
// ─────────────────────────────────────────────────────────────────────────────
class ApiService {

  // ==========================================================================
  // INTERNAL — auth token resolver
  // ==========================================================================

  _getToken() {
    const token =
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('token') ||
      sessionStorage.getItem('adminToken');
    // ✅ Silenced noisy per-call logging — was firing 100s of times per page load
    return token;
  }

  _adminHeaders(token) {
    const t = token || this._getToken();
    return { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' };
  }

  // ==========================================================================
  // TRANSFORMATION — Admin product
  // ==========================================================================

  transformAdminProductData(product) {
    try {
      const rawImage = product.primary_image || product.image || '';
      const imageUrl = (!rawImage || rawImage.startsWith('data:image/svg'))
        ? null
        : resolveImageUrl(rawImage, FALLBACK_THUMB);

      const status   = product.status || (product.is_active ? 'Active' : 'Inactive');
      const category = product.category_name || product.category || 'Uncategorized';
      const user     = product.posted_by_email || product.posted_by_username || product.user_email || 'Unknown';

      const image_count = Number(product.image_count  ?? product.images_count ?? product.photos ?? 0);
      const video_count = Number(product.video_count  ?? product.videos_count ?? 0);
      const like_count  = Number(product.like_count   ?? product.likes_count  ?? 0);

      const payment_amount = Number(product.payment_amount ?? product.payment ?? 0);
      const payment_status = product.payment_status ?? product.pay_status ?? 'Unpaid';

      const premium_plan = (
        product.premium_plan      ||
        product.plan_name         ||
        product.plan_type         ||
        product.plan              ||
        product.ad_plan           ||
        product.subscription_plan ||
        product.listing_plan      ||
        product.tier              ||
        'free'
      ).toLowerCase().trim();

      const date_added    = product.date_added || product.created_at || null;
      const validity_days = product.validity_days != null ? Number(product.validity_days) : undefined;

      return {
        id:    product.id,
        title: product.title || product.name || 'Untitled',
        price: parseFloat(product.price) || 0,
        category,
        category_name:      category,
        status,
        is_active:          product.is_active !== false,
        is_featured:        product.is_featured || false,
        user,
        posted_by_email:    product.posted_by_email    || null,
        posted_by_username: product.posted_by_username || null,
        contact:            product.contact  || product.phone || null,
        location:           product.city     || product.location || null,
        city:               product.city     || product.location || null,
        image:              imageUrl,
        primary_image:      imageUrl,
        primary_video:      product.primary_video   || null,
        video_thumbnail:    product.video_thumbnail || null,
        image_count,
        video_count,
        like_count,
        photos:         image_count,
        payment_amount,
        payment_status,
        premium_plan,
        validity_days,
        views:      product.views || 0,
        date_added,
        created_at: product.created_at || null,
        condition:  product.condition  || null,
        price_type: product.price_type || 'fixed',
      };
    } catch (error) {
      console.error('❌ transformAdminProductData error:', error, product);
      return {
        id: product?.id || Math.random(),
        title: product?.title || 'Untitled',
        price: 0, category: 'Uncategorized', category_name: 'Uncategorized',
        status: 'Active', is_active: true, is_featured: false,
        user: 'Unknown', posted_by_email: null, posted_by_username: null,
        contact: null, location: null, city: null,
        image: null, primary_image: null, primary_video: null, video_thumbnail: null,
        image_count: 0, video_count: 0, like_count: 0, photos: 0,
        payment_amount: 0, payment_status: 'Unpaid', premium_plan: 'free',
        validity_days: undefined,
        views: 0, date_added: null, created_at: null,
        condition: null, price_type: 'fixed',
      };
    }
  }

  formatDateForAdmin(dateString) {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return 'N/A'; }
  }

  // ==========================================================================
  // TRANSFORMATION — Public ad
  // ==========================================================================

  transformAdData(ad) {
    try {
      // ── Helper: extract the best URL from an image object ──────────────────
      // Backend returns AdImageSerializer objects: { id, image, image_url, is_primary }
      //   image_url = SerializerMethodField → always correct (Supabase or Railway)
      //   image     = raw ImageField value  → may be Supabase URL or relative path
      // We try image_url first (guaranteed correct), then fall back to image/url.
      const _extractImgUrl = (img) => {
        if (!img) return null;
        if (typeof img === 'string') return resolveImageUrl(img);
        // image_url is the safe serializer method field — prefer it
        const best = img.image_url || img.url || img.image || img.src || null;
        return best ? resolveImageUrl(best) : null;
      };

      // ── Primary image ──────────────────────────────────────────────────────
      // primary_image comes from get_primary_image() in the serializer — always
      // an absolute URL (Supabase or Railway). Use it directly if present.
      const primaryResolved = ad.primary_image
        ? resolveImageUrl(ad.primary_image)
        : (ad.img ? resolveImageUrl(ad.img) : FALLBACK_IMAGE);

      // ── Images array ───────────────────────────────────────────────────────
      const imagesResolved = Array.isArray(ad.images) && ad.images.length > 0
        ? ad.images.map(_extractImgUrl).filter(u => u && u !== FALLBACK_IMAGE)
        : [];

      // ── Videos array ──────────────────────────────────────────────────────
      const videosResolved = Array.isArray(ad.videos) && ad.videos.length > 0
        ? ad.videos
            .map(v => typeof v === 'string'
              ? resolveVideoUrl(v)
              : resolveVideoUrl(v.video_url || v.video || v.url))
            .filter(Boolean)
        : [];

      if (ad.primary_video) {
        const pv = resolveVideoUrl(ad.primary_video);
        if (pv && !videosResolved.includes(pv)) videosResolved.unshift(pv);
      }

      // ── Build final arrays ─────────────────────────────────────────────────
      // If we got resolved images from the array, use them.
      // If not, fall back to primary_image (which is correct from the serializer).
      // Only use FALLBACK_IMAGE if there truly are no images anywhere.
      const allImages = imagesResolved.length > 0
        ? imagesResolved
        : (primaryResolved !== FALLBACK_IMAGE ? [primaryResolved] : []);

      const allMedia = [...allImages, ...videosResolved];

      const primaryDisplay = allImages.length > 0
        ? allImages[0]
        : (videosResolved[0] || FALLBACK_IMAGE);

      const resolvedRating = (() => {
        const raw =
          ad.average_rating ?? ad.avg_rating ?? ad.seller_rating ??
          ad.rating_avg     ?? ad.rating     ?? ad.stars          ?? null;
        if (raw === null || raw === undefined) return null;
        const parsed = parseFloat(raw);
        return isNaN(parsed) || parsed === 0 ? null : parseFloat(parsed.toFixed(1));
      })();

      // ✅ Resolve seller profile image URL — backend now includes this on every ad
      const sellerProfileImageUrl =
        resolveAvatarUrl(
          ad.seller_profile_image_url ||
          ad.seller_avatar_url        ||
          ad.seller_profile_image     ||
          ad.seller_avatar            ||
          null
        );

      // ✅ Silenced per-ad log — was firing 127x per load

      return {
        id:    ad.id,
        name:  ad.title || ad.name || 'Untitled',
        title: ad.title || ad.name || 'Untitled',
        price: parseFloat(ad.price) || 0,
        category: ad.category_name || ad.category || 'Uncategorized',
        city:     ad.city || 'Not specified',
        location: ad.city || 'Not specified',
        img:    primaryDisplay,
        image:  primaryDisplay,
        images: allMedia,
        videos: videosResolved,

        rating:         resolvedRating,
        average_rating: ad.average_rating ?? null,
        rating_count:   ad.rating_count ?? ad.ratings_count ?? ad.review_count ?? ad.num_ratings ?? 0,
        reviewCount:    ad.review_count || ad.reviews_count || ad.rating_count || 0,

        views:              ad.views || 0,
        verified:           ad.verification_status === 'verified' || ad.is_verified || false,
        verificationStatus: ad.verification_status || (ad.is_verified ? 'verified' : 'unverified'),
        isFeatured:         ad.is_featured || false,
        postedTime:         this.formatPostedTime(ad.created_at),
        createdAt:          ad.created_at,
        description:        ad.description || ad.details || ad.body || '',

        seller:      ad.seller_name || ad.user_name || 'Anonymous Seller',
        sellerName:  ad.seller_name || ad.user_name || 'Anonymous Seller',
        seller_name: ad.seller_name || ad.user_name || 'Anonymous Seller',
        // ✅ First+last name for SellerSidebar display name
        seller_first_name: ad.seller_first_name || ad.poster_first_name || null,
        seller_last_name:  ad.seller_last_name  || ad.poster_last_name  || null,
        // ✅ NEW: seller profile image resolved to absolute URL
        seller_profile_image_url: sellerProfileImageUrl,

        contact:          ad.contact || ad.seller_phone || ad.contact_phone || '',
        email:            ad.seller_email || ad.contact_email || '',
        phone:            ad.contact || ad.seller_phone || ad.contact_phone || '',
        contact_number_1: ad.contact_number_1 || ad.contact || ad.seller_phone || '',

        sellerJoined:    this.formatMemberSinceDate(ad.seller_joined || ad.user_joined || ad.created_at),
        sellerJoinedRaw: ad.seller_joined || ad.user_joined || null,
        sellerListings:  ad.seller_listings || ad.user_listings || 0,
        seller_joined:   ad.seller_joined || ad.user_joined || null,
        seller_listings: ad.seller_listings || ad.user_listings || 0,
        responseTime:    ad.response_time || ad.seller_response_time || null,

        status: ad.status || 'active',

        is_liked:   ad.is_liked === true,
        like_count: ad.like_count ?? 0,

        price_type:         ad.price_type         || 'fixed',
        price_type_display: ad.price_type_display || null,
        condition:          ad.condition          || null,
        condition_display:  ad.condition_display  || null,
        category_name:      ad.category_name || ad.category || null,
      };
    } catch (error) {
      console.error('❌ transformAdData error:', error, ad);
      return {
        id: ad?.id || Math.random(),
        name: ad?.title || 'Untitled', title: ad?.title || 'Untitled',
        price: 0, category: 'Uncategorized', city: 'Not specified', location: 'Not specified',
        img: FALLBACK_IMAGE, image: FALLBACK_IMAGE, images: [FALLBACK_IMAGE], videos: [],
        rating: null, average_rating: null, rating_count: 0, reviewCount: 0,
        views: 0, verified: false, isFeatured: false, verificationStatus: 'unverified',
        condition: null, condition_display: null, price_type: 'fixed', price_type_display: null,
        postedTime: '2 hours ago', createdAt: new Date().toISOString(), description: '',
        seller: 'Anonymous Seller', sellerName: 'Anonymous Seller', seller_name: 'Anonymous Seller',
        seller_first_name: null, seller_last_name: null,
        seller_profile_image_url: null,   // ✅ NEW — safe fallback
        contact: '', email: '', phone: '', contact_number_1: '',
        sellerJoined: 'N/A', sellerJoinedRaw: null, sellerListings: 0,
        seller_joined: null, seller_listings: 0, responseTime: null, status: 'active',
        is_liked: false, like_count: 0, category_name: null,
      };
    }
  }

  formatPostedTime(dateString) {
    try {
      if (!dateString) return '2 hours ago';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '2 hours ago';
      const diffMs    = Date.now() - date;
      const diffMins  = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays  = Math.floor(diffMs / 86400000);
      const diffWeeks = Math.floor(diffMs / 604800000);
      if (diffMins  < 1)  return 'Just now';
      if (diffMins  < 60) return `${diffMins} minute${diffMins   === 1 ? '' : 's'} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours   === 1 ? '' : 's'} ago`;
      if (diffDays  < 7)  return `${diffDays} day${diffDays      === 1 ? '' : 's'} ago`;
      if (diffWeeks < 4)  return `${diffWeeks} week${diffWeeks   === 1 ? '' : 's'} ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '2 hours ago'; }
  }

  formatMemberSinceDate(dateString) {
    try {
      if (!dateString) return '1st Jan 2024';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '1st Jan 2024';
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const day = date.getDate();
      return `${day}${this.getOrdinalSuffix(day)} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch { return '1st Jan 2024'; }
  }

  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  // ==========================================================================
  // AUTHENTICATION
  // ==========================================================================


  // ── Constructor: spread all domain methods into this instance ────────────
  // Preserves 100% backwards compatibility — all apiService.xyz() calls keep
  // working without any changes in consuming components.
  constructor() {
    Object.assign(this, authMethods);
    Object.assign(this, adsMethods);
    Object.assign(this, socialMethods);
    Object.assign(this, offersMethods);
    Object.assign(this, messagesMethods);
    Object.assign(this, shopsMethods);
    Object.assign(this, profileMethods);
    Object.assign(this, chatMethods);
    Object.assign(this, referralsMethods);
    Object.assign(this, adminUsersMethods);
    Object.assign(this, adminAdsMethods);
    Object.assign(this, adminConvosMethods);
  }
}

// ── Singleton export ──────────────────────────────────────────────────────────
const apiService = new ApiService();
export default apiService;


























// /**
//  * services/api.js
//  * ─────────────────────────────────────────────────────────────────────────────
//  * Main API service — assembles all domain modules into one ApiService class.
//  *
//  * ARCHITECTURE — domain files (each ~100-200 lines):
//  *   apiUtils.js              — URL helpers, fallback images
//  *   authApi.js               — login, signup, logout, session
//  *   adsApi.js                — ads, categories, jobs
//  *   socialApi.js             — likes, follows, seller profiles
//  *   offersApi.js             — offer creation and negotiation
//  *   messagesApi.js           — DMs, ratings, notifications
//  *   shopsApi.js              — shop management
//  *   profileApi.js            — profile update, verification, password reset
//  *   chatApi.js               — live support chat
//  *   referralsApi.js          — referral stats, commissions, withdrawals
//  *   adminUsersApi.js         — admin: users, logs, shops
//  *   adminAdsApi.js           — admin: ad plans and validity
//  *   adminConversationsApi.js — admin: conversation moderation
//  *
//  * USAGE — unchanged, no refactor needed in any consuming file:
//  *   import apiService from './services/api';
//  *   await apiService.fetchAds();
//  *   await apiService.login(credentials);
//  * ─────────────────────────────────────────────────────────────────────────────
//  */

// import { API_BASE_URL, FALLBACK_IMAGE, FALLBACK_THUMB, resolveImageUrl, resolveVideoUrl, resolveAvatarUrl, _isHtmlResponse } from './apiUtils.js';
// import { methods as authMethods          } from './authApi.js';
// import { methods as adsMethods           } from './adsApi.js';
// import { methods as socialMethods        } from './socialApi.js';
// import { methods as offersMethods        } from './offersApi.js';
// import { methods as messagesMethods      } from './messagesApi.js';
// import { methods as shopsMethods         } from './shopsApi.js';
// import { methods as profileMethods       } from './profileApi.js';
// import { methods as chatMethods          } from './chatApi.js';
// import { methods as referralsMethods     } from './referralsApi.js';
// import { methods as adminUsersMethods    } from './adminUsersApi.js';
// import { methods as adminAdsMethods      } from './adminAdsApi.js';
// import { methods as adminConvosMethods   } from './adminConversationsApi.js';

// // ── ApiService class ──────────────────────────────────────────────────────────
// // Internal helpers (_getToken, _adminHeaders) and data transformers
// // (transformAdData, transformSingleAd) stay here because they are used
// // by fetchAds and getAdById — the core methods that all other code depends on.
// // ─────────────────────────────────────────────────────────────────────────────
// class ApiService {

//   // ==========================================================================
//   // INTERNAL — auth token resolver
//   // ==========================================================================

//   _getToken() {
//     const token =
//       localStorage.getItem('authToken') ||
//       localStorage.getItem('token') ||
//       localStorage.getItem('adminToken') ||
//       localStorage.getItem('admin_token') ||
//       localStorage.getItem('auth_token') ||
//       sessionStorage.getItem('authToken') ||
//       sessionStorage.getItem('token') ||
//       sessionStorage.getItem('adminToken');
//     // ✅ Silenced noisy per-call logging — was firing 100s of times per page load
//     return token;
//   }

//   _adminHeaders(token) {
//     const t = token || this._getToken();
//     return { 'Authorization': `Token ${t}`, 'Content-Type': 'application/json' };
//   }

//   // ==========================================================================
//   // TRANSFORMATION — Admin product
//   // ==========================================================================

//   transformAdminProductData(product) {
//     try {
//       const rawImage = product.primary_image || product.image || '';
//       const imageUrl = (!rawImage || rawImage.startsWith('data:image/svg'))
//         ? null
//         : resolveImageUrl(rawImage, FALLBACK_THUMB);

//       const status   = product.status || (product.is_active ? 'Active' : 'Inactive');
//       const category = product.category_name || product.category || 'Uncategorized';
//       const user     = product.posted_by_email || product.posted_by_username || product.user_email || 'Unknown';

//       const image_count = Number(product.image_count  ?? product.images_count ?? product.photos ?? 0);
//       const video_count = Number(product.video_count  ?? product.videos_count ?? 0);
//       const like_count  = Number(product.like_count   ?? product.likes_count  ?? 0);

//       const payment_amount = Number(product.payment_amount ?? product.payment ?? 0);
//       const payment_status = product.payment_status ?? product.pay_status ?? 'Unpaid';

//       const premium_plan = (
//         product.premium_plan      ||
//         product.plan_name         ||
//         product.plan_type         ||
//         product.plan              ||
//         product.ad_plan           ||
//         product.subscription_plan ||
//         product.listing_plan      ||
//         product.tier              ||
//         'free'
//       ).toLowerCase().trim();

//       const date_added    = product.date_added || product.created_at || null;
//       const validity_days = product.validity_days != null ? Number(product.validity_days) : undefined;

//       return {
//         id:    product.id,
//         title: product.title || product.name || 'Untitled',
//         price: parseFloat(product.price) || 0,
//         category,
//         category_name:      category,
//         status,
//         is_active:          product.is_active !== false,
//         is_featured:        product.is_featured || false,
//         user,
//         posted_by_email:    product.posted_by_email    || null,
//         posted_by_username: product.posted_by_username || null,
//         contact:            product.contact  || product.phone || null,
//         location:           product.city     || product.location || null,
//         city:               product.city     || product.location || null,
//         image:              imageUrl,
//         primary_image:      imageUrl,
//         primary_video:      product.primary_video   || null,
//         video_thumbnail:    product.video_thumbnail || null,
//         image_count,
//         video_count,
//         like_count,
//         photos:         image_count,
//         payment_amount,
//         payment_status,
//         premium_plan,
//         validity_days,
//         views:      product.views || 0,
//         date_added,
//         created_at: product.created_at || null,
//         condition:  product.condition  || null,
//         price_type: product.price_type || 'fixed',
//       };
//     } catch (error) {
//       console.error('❌ transformAdminProductData error:', error, product);
//       return {
//         id: product?.id || Math.random(),
//         title: product?.title || 'Untitled',
//         price: 0, category: 'Uncategorized', category_name: 'Uncategorized',
//         status: 'Active', is_active: true, is_featured: false,
//         user: 'Unknown', posted_by_email: null, posted_by_username: null,
//         contact: null, location: null, city: null,
//         image: null, primary_image: null, primary_video: null, video_thumbnail: null,
//         image_count: 0, video_count: 0, like_count: 0, photos: 0,
//         payment_amount: 0, payment_status: 'Unpaid', premium_plan: 'free',
//         validity_days: undefined,
//         views: 0, date_added: null, created_at: null,
//         condition: null, price_type: 'fixed',
//       };
//     }
//   }

//   formatDateForAdmin(dateString) {
//     try {
//       if (!dateString) return 'N/A';
//       const date = new Date(dateString);
//       if (isNaN(date.getTime())) return 'N/A';
//       return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
//     } catch { return 'N/A'; }
//   }

//   // ==========================================================================
//   // TRANSFORMATION — Public ad
//   // ==========================================================================

//   transformAdData(ad) {
//     try {
//       const primaryResolved = resolveImageUrl(ad.primary_image || ad.img);

//       const imagesResolved = Array.isArray(ad.images) && ad.images.length > 0
//         ? ad.images.map(img =>
//             resolveImageUrl(typeof img === 'string' ? img : (img.image_url || img.image || img.url))
//           ).filter(Boolean)
//         : [];

//       const videosResolved = Array.isArray(ad.videos) && ad.videos.length > 0
//         ? ad.videos
//             .map(v => typeof v === 'string' ? resolveVideoUrl(v) : resolveVideoUrl(v.video_url || v.video || v.url))
//             .filter(Boolean)
//         : [];

//       if (ad.primary_video) {
//         const pv = resolveVideoUrl(ad.primary_video);
//         if (pv && !videosResolved.includes(pv)) videosResolved.unshift(pv);
//       }

//       const allImages     = imagesResolved.length > 0 ? imagesResolved : [primaryResolved];
//       const allMedia      = [...allImages, ...videosResolved];
//       const primaryDisplay = allImages[0] !== FALLBACK_IMAGE
//         ? allImages[0]
//         : (videosResolved[0] || FALLBACK_IMAGE);

//       const resolvedRating = (() => {
//         const raw =
//           ad.average_rating ?? ad.avg_rating ?? ad.seller_rating ??
//           ad.rating_avg     ?? ad.rating     ?? ad.stars          ?? null;
//         if (raw === null || raw === undefined) return null;
//         const parsed = parseFloat(raw);
//         return isNaN(parsed) || parsed === 0 ? null : parseFloat(parsed.toFixed(1));
//       })();

//       // ✅ Resolve seller profile image URL — backend now includes this on every ad
//       const sellerProfileImageUrl =
//         resolveAvatarUrl(
//           ad.seller_profile_image_url ||
//           ad.seller_avatar_url        ||
//           ad.seller_profile_image     ||
//           ad.seller_avatar            ||
//           null
//         );

//       // ✅ Silenced per-ad log — was firing 127x per load

//       return {
//         id:    ad.id,
//         name:  ad.title || ad.name || 'Untitled',
//         title: ad.title || ad.name || 'Untitled',
//         price: parseFloat(ad.price) || 0,
//         category: ad.category_name || ad.category || 'Uncategorized',
//         city:     ad.city || 'Not specified',
//         location: ad.city || 'Not specified',
//         img:    primaryDisplay,
//         image:  primaryDisplay,
//         images: allMedia,
//         videos: videosResolved,

//         rating:         resolvedRating,
//         average_rating: ad.average_rating ?? null,
//         rating_count:   ad.rating_count ?? ad.ratings_count ?? ad.review_count ?? ad.num_ratings ?? 0,
//         reviewCount:    ad.review_count || ad.reviews_count || ad.rating_count || 0,

//         views:              ad.views || 0,
//         verified:           ad.is_verified || false,
//         verificationStatus: ad.verification_status || (ad.is_verified ? 'verified' : 'unverified'),
//         isFeatured:         ad.is_featured || false,
//         postedTime:         this.formatPostedTime(ad.created_at),
//         createdAt:          ad.created_at,
//         description:        ad.description || ad.details || ad.body || '',

//         seller:      ad.seller_name || ad.user_name || 'Anonymous Seller',
//         sellerName:  ad.seller_name || ad.user_name || 'Anonymous Seller',
//         seller_name: ad.seller_name || ad.user_name || 'Anonymous Seller',
//         // ✅ First+last name for SellerSidebar display name
//         seller_first_name: ad.seller_first_name || ad.poster_first_name || null,
//         seller_last_name:  ad.seller_last_name  || ad.poster_last_name  || null,
//         // ✅ NEW: seller profile image resolved to absolute URL
//         seller_profile_image_url: sellerProfileImageUrl,

//         contact:          ad.contact || ad.seller_phone || ad.contact_phone || '',
//         email:            ad.seller_email || ad.contact_email || '',
//         phone:            ad.contact || ad.seller_phone || ad.contact_phone || '',
//         contact_number_1: ad.contact_number_1 || ad.contact || ad.seller_phone || '',

//         sellerJoined:    this.formatMemberSinceDate(ad.seller_joined || ad.user_joined || ad.created_at),
//         sellerJoinedRaw: ad.seller_joined || ad.user_joined || null,
//         sellerListings:  ad.seller_listings || ad.user_listings || 0,
//         seller_joined:   ad.seller_joined || ad.user_joined || null,
//         seller_listings: ad.seller_listings || ad.user_listings || 0,
//         responseTime:    ad.response_time || ad.seller_response_time || null,

//         status: ad.status || 'active',

//         is_liked:   ad.is_liked === true,
//         like_count: ad.like_count ?? 0,

//         price_type:         ad.price_type         || 'fixed',
//         price_type_display: ad.price_type_display || null,
//         condition:          ad.condition          || null,
//         condition_display:  ad.condition_display  || null,
//         category_name:      ad.category_name || ad.category || null,
//       };
//     } catch (error) {
//       console.error('❌ transformAdData error:', error, ad);
//       return {
//         id: ad?.id || Math.random(),
//         name: ad?.title || 'Untitled', title: ad?.title || 'Untitled',
//         price: 0, category: 'Uncategorized', city: 'Not specified', location: 'Not specified',
//         img: FALLBACK_IMAGE, image: FALLBACK_IMAGE, images: [FALLBACK_IMAGE], videos: [],
//         rating: null, average_rating: null, rating_count: 0, reviewCount: 0,
//         views: 0, verified: false, isFeatured: false, verificationStatus: 'unverified',
//         condition: null, condition_display: null, price_type: 'fixed', price_type_display: null,
//         postedTime: '2 hours ago', createdAt: new Date().toISOString(), description: '',
//         seller: 'Anonymous Seller', sellerName: 'Anonymous Seller', seller_name: 'Anonymous Seller',
//         seller_first_name: null, seller_last_name: null,
//         seller_profile_image_url: null,   // ✅ NEW — safe fallback
//         contact: '', email: '', phone: '', contact_number_1: '',
//         sellerJoined: 'N/A', sellerJoinedRaw: null, sellerListings: 0,
//         seller_joined: null, seller_listings: 0, responseTime: null, status: 'active',
//         is_liked: false, like_count: 0, category_name: null,
//       };
//     }
//   }

//   formatPostedTime(dateString) {
//     try {
//       if (!dateString) return '2 hours ago';
//       const date = new Date(dateString);
//       if (isNaN(date.getTime())) return '2 hours ago';
//       const diffMs    = Date.now() - date;
//       const diffMins  = Math.floor(diffMs / 60000);
//       const diffHours = Math.floor(diffMs / 3600000);
//       const diffDays  = Math.floor(diffMs / 86400000);
//       const diffWeeks = Math.floor(diffMs / 604800000);
//       if (diffMins  < 1)  return 'Just now';
//       if (diffMins  < 60) return `${diffMins} minute${diffMins   === 1 ? '' : 's'} ago`;
//       if (diffHours < 24) return `${diffHours} hour${diffHours   === 1 ? '' : 's'} ago`;
//       if (diffDays  < 7)  return `${diffDays} day${diffDays      === 1 ? '' : 's'} ago`;
//       if (diffWeeks < 4)  return `${diffWeeks} week${diffWeeks   === 1 ? '' : 's'} ago`;
//       return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
//     } catch { return '2 hours ago'; }
//   }

//   formatMemberSinceDate(dateString) {
//     try {
//       if (!dateString) return '1st Jan 2024';
//       const date = new Date(dateString);
//       if (isNaN(date.getTime())) return '1st Jan 2024';
//       const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
//       const day = date.getDate();
//       return `${day}${this.getOrdinalSuffix(day)} ${months[date.getMonth()]} ${date.getFullYear()}`;
//     } catch { return '1st Jan 2024'; }
//   }

//   getOrdinalSuffix(day) {
//     if (day > 3 && day < 21) return 'th';
//     switch (day % 10) {
//       case 1: return 'st';
//       case 2: return 'nd';
//       case 3: return 'rd';
//       default: return 'th';
//     }
//   }

//   // ==========================================================================
//   // AUTHENTICATION
//   // ==========================================================================


//   // ── Constructor: spread all domain methods into this instance ────────────
//   // Preserves 100% backwards compatibility — all apiService.xyz() calls keep
//   // working without any changes in consuming components.
//   constructor() {
//     Object.assign(this, authMethods);
//     Object.assign(this, adsMethods);
//     Object.assign(this, socialMethods);
//     Object.assign(this, offersMethods);
//     Object.assign(this, messagesMethods);
//     Object.assign(this, shopsMethods);
//     Object.assign(this, profileMethods);
//     Object.assign(this, chatMethods);
//     Object.assign(this, referralsMethods);
//     Object.assign(this, adminUsersMethods);
//     Object.assign(this, adminAdsMethods);
//     Object.assign(this, adminConvosMethods);
//   }
// }

// // ── Singleton export ──────────────────────────────────────────────────────────
// const apiService = new ApiService();
// export default apiService;

